import { Grid } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import { useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Spin from 'components/Spin';
import { Formik, Form, Field } from 'formik';
import React, { useState, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';
import { of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import DialogTitle from 'v2/components/Shared/DialogTitle';
import { ListRadioButtonFormField } from 'v2/components/Shared/FormFields/ListRadioButton';
import { refundRequestFactory } from 'v2/services/shopping/refundRequest';

import useStyles from './styles';
import { RefundBuyerRequestSchema } from './validations';
import Transition from '../Transition';

import type { FormikProps } from 'formik';

export interface IBuyerRefundReason {
  motive: string;
  detail: string;
  invoiceId: string;
}

export interface IProducerCoproducerRefundReason {
  invoiceId: string;
}

interface IProps {
  open: boolean;
  invoiceId: string | number;
  onClose: () => void;
  onFinish: (refundReason: IBuyerRefundReason) => void;
}

export default function BuyerRefundRequestModal({ open, invoiceId, onClose, onFinish }: IProps) {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [refundReason, setRefundReason] = useState<IBuyerRefundReason>();

  const [onSubmit] = useCallbackObservable((values: IBuyerRefundReason, { setSubmitting, resetForm }: FormikProps<IBuyerRefundReason>) => {
    setSubmitting(true);

    return refundRequestFactory.sendBuyerRefundRequest(values).pipe(
      tap((response) => {
        const refundReason: IBuyerRefundReason = {
          ...values,
          ...{ success: response.success, motive: response.request_motive_id, detail: response.description, invoiceId: response.invoice_id },
        };
        setRefundReason(refundReason);
        setSubmitting(false);
        resetForm();
        toast.success('Solicitação realizada com sucesso!');
        onComplete();
      }),
      catchError((err) => {
        setSubmitting(false);
        toast.error(err);
        return of(true);
      })
    );
  }, []);

  const onComplete = useCallback(() => {
    onFinish(refundReason);
    setRefundReason(null);
  }, [onFinish, refundReason]);

  const onHandleClose = useCallback(
    (resetForm) => {
      setRefundReason(null);
      onClose();
      resetForm();
    },
    [onClose]
  );

  const [motives] = useObservable(() => refundRequestFactory.getRefundMotive(), []);
  const [loading] = useObservable(() => refundRequestFactory.loading(), []);

  const motivesOptions = useMemo(
    () =>
      (motives || []).map((item) => ({
        label: item.description,
        value: item.id,
      })),
    [motives]
  );

  return (
    <Formik validationSchema={RefundBuyerRequestSchema} initialValues={{ motive: '', detail: '', invoiceId }} onSubmit={onSubmit}>
      {({ isSubmitting, submitForm, isValid, dirty, resetForm }) => {
        const shouldDisableConfirmBtn = !isValid || !dirty || isSubmitting;

        return (
          <Form>
            <Dialog
              TransitionComponent={Transition}
              aria-labelledby='customized-dialog-title'
              open={open}
              onClose={() => onHandleClose(resetForm)}
              fullScreen={isMobile}
              fullWidth
            >
              <DialogTitle id='customized-dialog-title' onClose={() => onHandleClose(resetForm)}>
                <Typography variant='h6'>Motivo do reembolso</Typography>
              </DialogTitle>
              <DialogContent className={classes.dialogContent} dividers>
                {loading || isSubmitting ? (
                  <div style={{ textAlign: 'center', padding: 16 }}>
                    <Spin />
                  </div>
                ) : (
                  <div className={classes.root}>
                    <Grid item xs={12}>
                      <Field id='motive' name='motive' options={motivesOptions} component={ListRadioButtonFormField} />
                    </Grid>
                  </div>
                )}
              </DialogContent>
              <DialogActions>
                <Button color='default' onClick={() => onHandleClose(resetForm)}>
                  Cancelar
                </Button>
                <Button
                  onClick={submitForm}
                  type='submit'
                  variant='contained'
                  color='secondary'
                  disabled={shouldDisableConfirmBtn}
                  className={classes.button}
                >
                  Solicitar
                </Button>
              </DialogActions>
            </Dialog>
          </Form>
        );
      }}
    </Formik>
  );
}
